import React from 'react';
import { subDays } from 'date-fns';
import { isEmpty, get } from 'lodash';
import { findCountriesByISO3, getCitizenship } from '../../countryHelper';
import { ARMENIA_ISO3, DATE_FORMAT_DISPLAY, DATE_FORMAT_SHORT, LANGUAGES } from '../../../constants';
import { reformatDate } from '../../dates';
import { getApplicantCitizenship } from '../index';
import PROPERTY_TYPES from '../../../constants/propertyTypes';

export const updatePropertyClaimForm = (
  status,
  countries,
  dispatch,
  updatePropertyForm,
  applicant,
) => {
  if (!isEmpty(applicant) && countries.length) {
    dispatch(
      updatePropertyForm({
        name: 'personalData',
        data: {
          applicantType: status,
          applicant,
          ...applicant,
          ...getCitizenship(applicant.citizenship, countries),
        },
      }),
    );
    dispatch(
      updatePropertyForm({
        name: 'accidentInfo',
        applicant,
        data: {
          ...applicant,
          startDate: subDays(new Date(applicant.contractStartDate), 1),
          endDate: new Date(applicant.contractEndDate),
        },
      }),
    );
    dispatch(
      updatePropertyForm({
        name: 'compensation',
        data: {
          bankAccount: applicant.accountNumber,
          isPhysicalEntity: status === 1,
        },
      }),
    );
    dispatch(
      updatePropertyForm({
        name: 'conclusion',
        data: {
          email: applicant.email,
          emailConfirmation: applicant.email,
          phone: applicant.phone,
          cellPhone: applicant.mobilePhone,
          citizenship: getApplicantCitizenship(
            status,
            applicant.citizenship,
            countries,
          ).citizenship,
        },
      }),
    );
  }
};

const buildAddressString = (location, suffix = '') => {
  const addressParts = [];
  addressParts.push(location[`country${suffix}`] || '');
  addressParts.push(location[`location1${suffix}`] || '');
  if (location.location2EN !== 'Yerevan') {
    addressParts.push(location[`location2${suffix}`] || '');
  }
  addressParts.push(location[`location3${suffix}`] || '');
  addressParts.push(location[`location4${suffix}`] || '');
  addressParts.push(location[`street${suffix}`] || '');
  addressParts.push(location[`addressString${suffix}`] || '');

  const address = addressParts.join(' ').trim();
  return address.length === 0 ? null : address;
};

const getAddressFromLocation = (location = {}) => {
  const address = buildAddressString(location);
  const addressEn = buildAddressString(location, 'EN');
  const addressRu = buildAddressString(location, 'RU');

  return [address, addressEn, addressRu];
};

const getInsuredAddress = (applicant = {}) => {
  const address = [];
  address.push(applicant.insuredCountry || '');
  address.push(applicant.insuredCityVillage || '');
  address.push(applicant.insuredAddress || '');
  return address.join(' ');
};

const matchPropertyOwner = (owner, socialCard, passportNum) => {
  if (owner.sameInsured === '1') {
    return true;
  }

  if (owner.socialCard === socialCard) {
    return true;
  }

  const ownerPassports = get(owner, 'prownPersonPassport');
  return ownerPassports.some((passport) => passport.passportNum === passportNum);
};

const getApplicantName = (person, insuredPerson, isSameInsured) => ({
  firstNameHY: isSameInsured ? insuredPerson.insuredFirstName : person.firstName,
  firstNameEN: isSameInsured ? '' : person.firstNameEN,
  firstNameRU: isSameInsured ? '' : person.firstNameRU,
  lastNameHY: isSameInsured ? insuredPerson.insuredLastName : person.lastName,
  lastNameEN: isSameInsured ? '' : person.lastNameEN,
  lastNameRU: isSameInsured ? '' : person.lastNameRU,
});

const getApplicantPassport = (person, insuredPerson, isSameInsured) => ({
  passport: isSameInsured ? insuredPerson.insuredPassportNum : person.passportNum,
  passportGiven: isSameInsured ? insuredPerson.issueDate : person.issueDate,
  passportValidUntil: isSameInsured ? insuredPerson.expiryDate : person.expiryDate,
  passportFrom: isSameInsured ? insuredPerson.authority : person.authority,
  citizenship: isSameInsured ? insuredPerson.citizenship : person.citizenship,
});

export const mapApplicant = (masysApplicant, requestParams) => {
  const applicant = {};
  const { ssn, pnum } = requestParams;

  applicant.policyNumber = masysApplicant.policyNumber;

  const propertyOwnerPerson = get(masysApplicant, 'dynamicObject.propertyOwnerPerson', [])
    .find((owner) => matchPropertyOwner(owner, ssn, pnum)) || {};

  const isSameInsured = propertyOwnerPerson.sameInsured === '1';

  Object.assign(applicant, getApplicantName(propertyOwnerPerson, masysApplicant, isSameInsured));

  const propertyOwnerPassport = get(propertyOwnerPerson, 'prownPersonPassport[0]', {});

  Object.assign(applicant, getApplicantPassport(propertyOwnerPassport, masysApplicant, isSameInsured));

  const propertyOwnerLocation = get(propertyOwnerPerson, 'prownPersonLocation[0]', {});
  const [physAddress, physAddressEn, physAddressRu] = getAddressFromLocation(propertyOwnerLocation);

  if (isSameInsured) {
    applicant.physAddressHY = getInsuredAddress(masysApplicant);
    applicant.physAddressEN = '';
    applicant.physAddressRU = '';
  } else {
    applicant.physAddressHY = physAddress;
    applicant.physAddressEN = physAddressEn;
    applicant.physAddressRU = physAddressRu;
  }

  const propertyOwnerContacts = get(propertyOwnerPerson, 'prownPersonPersonalData[0]', {});
  const { email, mobileNumber, phoneNumber } = propertyOwnerContacts;
  applicant.email = email || masysApplicant.insuredEmail || null;
  applicant.mobilePhone = mobileNumber || masysApplicant.insuredMobilePhone || null;
  applicant.phone = phoneNumber || masysApplicant.insuredPhone || null;

  const propertyOwnerOrganization = get(masysApplicant, 'dynamicObject.propertyOwnerOrganization[0]', {});
  const orgIsSameInsured = propertyOwnerOrganization.sameInsured === '1';

  applicant.hvhh = orgIsSameInsured ? masysApplicant.insuredOrganizationHvhh : propertyOwnerOrganization.tin;
  applicant.organizationNameHY = orgIsSameInsured ? masysApplicant.insuredOrganizationName : propertyOwnerOrganization.organizationName;
  applicant.organizationNameEN = orgIsSameInsured ? '' : propertyOwnerOrganization.organizationNameEN;
  applicant.organizationNameRU = orgIsSameInsured ? '' : propertyOwnerOrganization.organizationNameRU;

  const propertyOwnerOrganizationLocation = (Array.isArray(get(propertyOwnerOrganization, 'prownOrgLocation', []))
    ? get(propertyOwnerOrganization, 'prownOrgLocation', []).find((l) => l.locationTypeId === '1')
    : get(propertyOwnerOrganization, 'prownOrgLocation', {})) || {};

  const [legalAddress, legalAddressEn, legalAddressRu] = getAddressFromLocation(propertyOwnerOrganizationLocation);
  if (!orgIsSameInsured) {
    applicant.legalAddressHY = legalAddress;
    applicant.legalAddressEN = legalAddressEn;
    applicant.legalAddressRU = legalAddressRu;
  }

  applicant.realEstates = get(masysApplicant, 'dynamicObject.propertyRealEstate[0].realEstateObject', []);
  applicant.realEstateLocation = get(masysApplicant, 'dynamicObject.propertyRealEstate[0].realEstateObject[0].realEstateLocation[0]', []);
  applicant.properties = get(masysApplicant, 'dynamicObject.propertyRealEstate[0].property', []);

  const { startDate, endDate } = masysApplicant;
  applicant.contractStartDate = startDate;
  applicant.contractEndDate = endDate;
  const formattedStartDate = startDate ? reformatDate(startDate, DATE_FORMAT_SHORT, DATE_FORMAT_DISPLAY) : null;
  const formattedEndDate = endDate ? reformatDate(endDate, DATE_FORMAT_SHORT, DATE_FORMAT_DISPLAY) : null;
  applicant.contractTerm = `${formattedStartDate} - ${formattedEndDate}`;

  applicant.assuredOrganizationHvhh = masysApplicant.assuredOrganizationHvhh;
  applicant.insuredOrganizationHvhh = masysApplicant.insuredOrganizationHvhh || masysApplicant.assuredOrganizationHvhh;

  return { applicant };
};

const getAccidentStreet = (initialData, formLanguage) => {
  let address = '';
  if (!isEmpty(initialData.realEstateLocation)) {
    const { location1EN, location2EN, location2, addressString, addressStringEN } = initialData.realEstateLocation;

    if (location1EN === 'Yerevan') {
      if (formLanguage === LANGUAGES.HY) {
        address = `${location2 || ''} ${addressString || ''}`;
      } else if (formLanguage === LANGUAGES.EN) {
        address = `${location2EN || ''} ${addressStringEN || ''}`;
      }
    } else if (formLanguage === LANGUAGES.HY) {
      address = addressString || '';
    } else if (formLanguage === LANGUAGES.EN) {
      address = addressStringEN || '';
    }
  }

  return address.trim();
};

export const bindPropertyClaimAccidentAddress = (initialData, formLanguage, sortedStates, sortedCountries) => ({
  isAccInArmenia: true,
  accCountry:
      initialData.accCountry ? initialData.accCountry : findCountriesByISO3([ARMENIA_ISO3], sortedCountries),
  accState: initialData.accState ? initialData.accState : (initialData
      && initialData.realEstateLocation
      && initialData.realEstateLocation.location1Id
      && sortedStates.find(
        (state) => (
          state.masysId.toString() === initialData.realEstateLocation.location1Id.toString()
        ),
      )),
  accVillage: initialData.accVillage || null,
  accStreet: initialData.accStreet ? initialData.accStreet : getAccidentStreet(initialData, formLanguage),
});

const createSuccessTableAddress = (data, language) => {
  const langKey = language.toUpperCase();
  const dataAddres = data.personalData.realEstateLocation;
  const location1 = langKey === LANGUAGES.HY.toUpperCase() ? dataAddres.location1 : dataAddres[`location1${langKey}`];
  const location2 = langKey === LANGUAGES.HY.toUpperCase() ? dataAddres.location2 : dataAddres[`location2${langKey}`];
  const addressString = langKey === LANGUAGES.HY.toUpperCase() ? dataAddres.addressString : dataAddres[`addressString${langKey}`];

  return [location1, location2, addressString].filter(Boolean).join(', ');
};

export const bindPropertyClaimConclusionSuccessTableData = (data) => {
  const { policyNumber } = data.personalData;
  const type = PROPERTY_TYPES[data.personalData.realEstates[0].realEstateTypeId] || {};
  const typeText = type[`name${data.language.toUpperCase()}`] || '';
  const addressString = createSuccessTableAddress(data, data.language);

  return [
    {
      title: 'claim.conclusion.successTable.contractNumber',
      data: policyNumber,
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.propertyType',
      data: (
        <span>{typeText}</span>
      ),
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.addressProperty',
      data: (
        <span>{addressString}</span>
      ),
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.accidentInfo',
      data: <div>{data.accidentInfo.accidentDetails}</div>,
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.damageDescription',
      data: <div>{data.accidentInfo.accidentPrevention}</div>,
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.damageAmount',
      data: (
        <span>{data.accidentInfo.damageAmount}</span>
      ),
      divider: <hr />,
    },
  ];
};
